
import { useState, useEffect, useMemo, useCallback } from 'react';
import {
  UserContent, UserContentFolder, WaypointContent,
  TrackContent, LineContent, AreaContent, ContentType,
  UserWaypoint, SpotVisit
} from '../types';
import {
  subscribeToUserContent, subscribeToUserFolders
} from '../services/myContentService';
import {
  collection, query, where, onSnapshot
} from 'firebase/firestore';
import { db } from '../services/firebaseService';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../services/firebaseService';

import { BRAND } from '../constants/mapColors';
// Lazy migration: call once per session
const migratedSessions = new Set<string>();
const MY_CONTENT_VERBOSE_LOGS = false;

const myContentDebugLog = (...args: unknown[]) => {
  if (MY_CONTENT_VERBOSE_LOGS) console.log(...args);
};

/**
 * Subscribe to legacy user_tracks collection.
 * Uses only where() (no orderBy) to avoid composite index requirements.
 */
function subscribeToLegacyTracks(
  userId: string,
  callback: (tracks: TrackContent[]) => void
): () => void {
  const q = query(
    collection(db, 'user_tracks'),
    where('userId', '==', userId)
  );
  return onSnapshot(q, (snap) => {
    myContentDebugLog(`[useMyContent] Legacy tracks snapshot: ${snap.docs.length} docs`);
    const tracks: TrackContent[] = snap.docs.map(d => {
      const data = d.data();
      return {
        id: `legacy_track_${d.id}`,
        userId,
        type: 'track' as const,
        name: data.name || 'Min tur',
        color: BRAND,
        distance: Number(data.distance || 0),
        duration: Number(data.duration || 0),
        elevationGain: Number(data.elevationGain || 0),
        elevationLoss: Number(data.elevationLoss || 0),
        pointCount: Number(data.pointCount || 0),
        gpxUrl: data.gpxUrl || '',
        startLocation: data.startLocation || { latitude: 0, longitude: 0 },
        linkedRouteId: data.linkedRouteId || undefined,
        bounds: data.bounds || undefined,
        folderId: null,
        createdAt: Number(data.createdAt || Date.now()),
        updatedAt: Number(data.createdAt || Date.now()),
      };
    });
    // Sort client-side (newest first)
    tracks.sort((a, b) => b.createdAt - a.createdAt);
    callback(tracks);
  }, (err) => {
    console.error('[useMyContent] Legacy tracks subscription FAILED:', err.code, err.message);
    callback([]);
  });
}

/**
 * Subscribe to legacy user_waypoints collection.
 * Uses only where() (no orderBy) to avoid composite index requirements.
 */
function subscribeToLegacyWaypoints(
  userId: string,
  callback: (waypoints: UserWaypoint[]) => void
): () => void {
  const q = query(
    collection(db, 'user_waypoints'),
    where('userId', '==', userId)
  );
  return onSnapshot(q, (snap) => {
    myContentDebugLog(`[useMyContent] Legacy waypoints snapshot: ${snap.docs.length} docs`);
    const waypoints: UserWaypoint[] = snap.docs.map(d => ({
      id: d.id,
      ...(d.data() as Omit<UserWaypoint, 'id'>),
    }));
    // Sort client-side (newest first)
    waypoints.sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
    callback(waypoints);
  }, (err) => {
    console.error('[useMyContent] Legacy waypoints subscription FAILED:', err.code, err.message);
    callback([]);
  });
}

export function useMyContent(userId: string | null, visits: SpotVisit[] = []) {
  const [newContent, setNewContent] = useState<UserContent[]>([]);
  const [legacyWaypoints, setLegacyWaypoints] = useState<UserWaypoint[]>([]);
  const [legacyTracks, setLegacyTracks] = useState<TrackContent[]>([]);
  const [folders, setFolders] = useState<UserContentFolder[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [reviewedSpotIds, setReviewedSpotIds] = useState<Set<string>>(new Set());

  // Subscribe to user's reviews to know which visits have been reviewed
  useEffect(() => {
    if (!userId) { setReviewedSpotIds(new Set()); return; }
    const q = query(collection(db, 'reviews'), where('userId', '==', userId));
    const unsub = onSnapshot(q, (snap) => {
      const ids = new Set<string>();
      snap.docs.forEach(d => { const spotId = d.data().spotId; if (spotId) ids.add(spotId); });
      setReviewedSpotIds(ids);
    }, () => {});
    return unsub;
  }, [userId]);

  // Trigger lazy migration once per user per session
  useEffect(() => {
    if (!userId || migratedSessions.has(userId)) return;
    migratedSessions.add(userId);
    try {
      const migrateFn = httpsCallable(functions, 'migrateUserContent');
      migrateFn().catch(() => {}); // fire-and-forget
    } catch {
      // Ignore — migration is optional
    }
  }, [userId]);

  useEffect(() => {
    if (!userId) {
      setNewContent([]);
      setLegacyWaypoints([]);
      setLegacyTracks([]);
      setFolders([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    let readyCount = 0;
    const totalSources = 4;
    const unsubs: (() => void)[] = [];

    const markReady = () => {
      readyCount++;
      if (readyCount >= totalSources) setIsLoading(false);
    };

    // 1. New content (user_content collection) — may fail if no composite index yet
    try {
      const unsub = subscribeToUserContent(userId, (items) => {
        setNewContent(items);
        markReady();
      });
      unsubs.push(unsub);
    } catch (err: any) {
      console.error('[useMyContent] user_content subscribe failed:', err.message);
      markReady(); // count as ready even if failed
    }

    // 2. Folders (user_content_folders collection)
    try {
      const unsub = subscribeToUserFolders(userId, (f) => {
        setFolders(f);
        markReady();
      });
      unsubs.push(unsub);
    } catch (err: any) {
      console.error('[useMyContent] folders subscribe failed:', err.message);
      markReady();
    }

    // 3. Legacy waypoints (user_waypoints collection)
    try {
      const unsub = subscribeToLegacyWaypoints(userId, (wps) => {
        setLegacyWaypoints(wps);
        markReady();
      });
      unsubs.push(unsub);
    } catch (err: any) {
      console.error('[useMyContent] legacy waypoints subscribe failed:', err.message);
      markReady();
    }

    // 4. Legacy tracks (user_tracks collection)
    try {
      const unsub = subscribeToLegacyTracks(userId, (trks) => {
        setLegacyTracks(trks);
        markReady();
      });
      unsubs.push(unsub);
    } catch (err: any) {
      console.error('[useMyContent] legacy tracks subscribe failed:', err.message);
      markReady();
    }

    // Safety timeout: ensure isLoading becomes false even if subscriptions never fire
    const safetyTimer = setTimeout(() => {
      if (readyCount < totalSources) {
        console.warn(`[useMyContent] Safety timeout: only ${readyCount}/${totalSources} sources ready, forcing load complete`);
        setIsLoading(false);
      }
    }, 10000);

    return () => {
      clearTimeout(safetyTimer);
      unsubs.forEach(fn => fn());
    };
  }, [userId]);

  // Bridge visited spots as WaypointContent
  const visitWaypoints = useMemo((): WaypointContent[] => {
    if (!userId) return [];
    return visits.map(v => ({
      id: `visit_${v.spotId}`,
      userId,
      type: 'waypoint' as const,
      name: v.spotName || 'Besøkt sted',
      icon: 'camp' as const,
      color: BRAND,
      latitude: v.latitude,
      longitude: v.longitude,
      folderId: null,
      createdAt: v.visitedAt || Date.now(),
      updatedAt: v.visitedAt || Date.now(),
      bounds: { latMin: v.latitude, latMax: v.latitude, lonMin: v.longitude, lonMax: v.longitude },
      _source: 'visit' as const,
      _spotId: v.spotId,
      _county: v.county,
      _photoUrl: v.photoUrl,
    }));
  }, [visits, userId]);

  // Merge new content with legacy data, deduplicating by _legacyId
  const content = useMemo(() => {
    // Debug logging removed — was spamming console on every useMemo re-evaluation
    // Collect IDs of items that have already been migrated (have _legacyId in new content)
    const migratedLegacyIds = new Set<string>();
    for (const item of newContent) {
      const legacyId = (item as any)._legacyId;
      if (legacyId) migratedLegacyIds.add(legacyId);
    }

    // Convert legacy waypoints to WaypointContent format, skip already-migrated ones
    const bridgedWaypoints: WaypointContent[] = legacyWaypoints
      .filter(wp => !migratedLegacyIds.has(wp.id))
      .map(wp => ({
        id: `legacy_wp_${wp.id}`,
        userId: wp.userId,
        type: 'waypoint' as const,
        name: wp.name || 'Mitt punkt',
        note: wp.note || undefined,
        icon: wp.icon || 'pin',
        color: wp.color || '#ef4444',
        latitude: wp.latitude,
        longitude: wp.longitude,
        folderId: null,
        createdAt: wp.createdAt || Date.now(),
        updatedAt: wp.createdAt || Date.now(),
        bounds: { latMin: wp.latitude, latMax: wp.latitude, lonMin: wp.longitude, lonMax: wp.longitude },
        linkedSpotId: wp.linkedSpotId,
      }));

    // Legacy tracks — filter out already-migrated ones
    const bridgedTracks = legacyTracks.filter(t => {
      const realId = t.id.replace('legacy_track_', '');
      return !migratedLegacyIds.has(realId);
    });

    const merged = [...newContent, ...bridgedWaypoints, ...visitWaypoints, ...bridgedTracks];
    return merged;
  }, [newContent, legacyWaypoints, legacyTracks, visitWaypoints]);

  const waypoints = useMemo(
    () => content.filter((c): c is WaypointContent => c.type === 'waypoint'),
    [content]
  );
  const tracks = useMemo(
    () => content.filter((c): c is TrackContent => c.type === 'track'),
    [content]
  );
  const lines = useMemo(
    () => content.filter((c): c is LineContent => c.type === 'line'),
    [content]
  );
  const areas = useMemo(
    () => content.filter((c): c is AreaContent => c.type === 'area'),
    [content]
  );

  const getContentByFolder = useCallback(
    (folderId: string | null) => content.filter(c => (c.folderId ?? null) === folderId),
    [content]
  );

  const getContentByType = useCallback(
    (type: ContentType) => content.filter(c => c.type === type),
    [content]
  );

  return {
    content,
    folders,
    waypoints,
    tracks,
    lines,
    areas,
    isLoading,
    reviewedSpotIds,
    getContentByFolder,
    getContentByType,
  };
}
